// A full-page (not modal) rendering of the incident response fan-out — the
// same staged, fixed-timeline animation OutageFanoutOverlay.tsx runs (see
// outageFanout.ts's FANOUT_STEPS and its header comment for why this is a
// choreographed walkthrough rather than live per-channel status), but laid
// out as a horizontal pipeline and driven entirely by the URL so every state
// is shareable and directly linkable:
//
//   /outage-pipeline                          → scenario picker
//   /outage-pipeline/:incidentId              → the pipeline for that incident
//   /outage-pipeline/:incidentId/:channel     → pipeline + that channel's mockup on top
//
// This one component owns all three shapes via useParams() — it does not
// assume App.tsx wires nested <Route> elements for :incidentId/:channel.
import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from '../authHeaders';
import IncidentDeclaredMockup from '../components/IncidentDeclaredMockup';
import TeamsPostMockup from '../components/TeamsPostMockup';
import PagerDutyAlertMockup from '../components/PagerDutyAlertMockup';
import { ServiceNowTicketModal } from '../components/ServiceNowTicketModal';
import AIReportMockup from '../components/AIReportMockup';
import IncidentEmailMockup from '../components/IncidentEmailMockup';
import FtbAutomationMockup from '../components/FtbAutomationMockup';
import {
  isFtbAutomationIncident,
  resolutionLogLineForIncident,
  SIMULATED_OUTAGE_SCENARIOS,
  STEP_COLORS,
  stepLogLinesForIncident,
  stepsForIncident,
  triggerLogLine,
  type FanoutIncident,
  type LogLine,
  type MockupKey,
  toTicketIncident,
} from '../lib/outageFanout';
import { DoneBadge, StepIcon } from './outagePipelineIcons';
import '../styles/outage-pipeline.css';

const MOCKUP_KEYS: MockupKey[] = [
  'declared',
  'pagerduty',
  'teams',
  'servicenow',
  'ai-report',
  'email',
  'ftb-notification',
  'ftb-snow-lookup',
  'ftb-sharepoint-lookup',
  'ftb-requirement',
  'ftb-notice',
];

function isMockupKey(v: string | undefined): v is MockupKey {
  return !!v && (MOCKUP_KEYS as string[]).includes(v);
}

function renderMockup(key: MockupKey, incident: FanoutIncident, onClose: () => void) {
  switch (key) {
    case 'declared':
      return <IncidentDeclaredMockup incident={incident} onClose={onClose} />;
    case 'pagerduty':
      return <PagerDutyAlertMockup incident={incident as any} onClose={onClose} />;
    case 'teams':
      return <TeamsPostMockup incident={incident as any} onClose={onClose} />;
    case 'servicenow':
      return <ServiceNowTicketModal incident={toTicketIncident(incident)} onClose={onClose} />;
    case 'ai-report':
      return <AIReportMockup incident={incident as any} onClose={onClose} />;
    case 'email':
      return <IncidentEmailMockup incident={incident as any} onClose={onClose} />;
    case 'ftb-notification':
      return <FtbAutomationMockup section="notification" onClose={onClose} />;
    case 'ftb-snow-lookup':
      return <FtbAutomationMockup section="snow-lookup" onClose={onClose} />;
    case 'ftb-sharepoint-lookup':
      return <FtbAutomationMockup section="sharepoint-lookup" onClose={onClose} />;
    case 'ftb-requirement':
      return <FtbAutomationMockup section="requirement" onClose={onClose} />;
    case 'ftb-notice':
      return <FtbAutomationMockup section="notice" onClose={onClose} />;
    default:
      return null;
  }
}

export default function OutagePipelinePage() {
  const { incidentId, channel } = useParams<{ incidentId?: string; channel?: string }>();

  if (!incidentId) {
    return <ScenarioPicker />;
  }

  return <PipelineView incidentId={incidentId} channel={channel} />;
}

/* ── state 1: no incident yet ────────────────────────────────────────── */

function ScenarioPicker() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trigger = useCallback(
    async (scenarioKey: string) => {
      setPending(scenarioKey);
      setError(null);
      try {
        const res = await pbAuthedFetch('/api/outages/simulate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scenario: scenarioKey }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Request failed: ${res.status}`);
        }
        const body = await res.json().catch(() => ({}));
        const id = body.incident?.id;
        if (!id) throw new Error('Simulate response did not include an incident id');
        // Real navigation — not setState-and-render-inline — so the pipeline
        // for this incident is a real, shareable URL.
        navigate(`/outage-pipeline/${id}`);
      } catch (err) {
        setError((err as Error).message);
        setPending(null);
      }
    },
    [navigate],
  );

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Incident Response</p>
            <h1 className="pagehead__title">Simulate Outage</h1>
            <div className="pagehead__facts">
              <span className="fact">Choose a scenario to walk through the full incident response pipeline</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {error && (
          <div className="notice notice--risk" style={{ marginBottom: 'var(--sp-4)' }}>
            {error}
          </div>
        )}

        <div className="pipeline-picker-grid">
          {SIMULATED_OUTAGE_SCENARIOS.map((s) => (
            <button
              key={s.key}
              type="button"
              className="pipeline-picker-card"
              disabled={!!pending}
              onClick={() => trigger(s.key)}
            >
              <span className="pipeline-picker-name">{s.label}</span>
              <span className="pipeline-picker-desc">{s.description}</span>
              <span className="pipeline-picker-arrow">{pending === s.key ? 'Simulating…' : 'Run scenario →'}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── states 2 & 3: incident chosen, optionally a channel's mockup on top ── */

function PipelineView({ incidentId, channel }: { incidentId: string; channel?: string }) {
  const navigate = useNavigate();

  // Real-fetched via react-query rather than assumed to already be in memory
  // — this page is just as often reached by a direct/shared link as by a
  // fresh "Simulate Outage" navigation.
  const { data, isLoading, error } = useQuery({
    queryKey: ['mim-incident', incidentId],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/mim/${incidentId}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const incident: FanoutIncident = data?.incident || {};

  // Every scenario runs FANOUT_STEPS' 6 generic channels, except
  // ftb-automation, which runs its own 3-step docx-derived pipeline instead.
  const isFtb = isFtbAutomationIncident(incident);
  const steps = stepsForIncident(incident);
  const stepLogLines = stepLogLinesForIncident(incident);

  const [stepIndex, setStepIndex] = useState(0);
  const done = stepIndex >= steps.length;

  useEffect(() => {
    if (done) return;
    const timer = setTimeout(() => setStepIndex((i) => i + 1), steps[stepIndex].delayMs);
    return () => clearTimeout(timer);
  }, [stepIndex, done]);

  // Event log — narrates the same fan-out the pipeline animates, starting
  // from the monitoring alert that supposedly triggered it. Keyed on the
  // incident id (not the incident object, which is a fresh `{}` reference
  // every render until react-query resolves) so the trigger line fires
  // exactly once per incident, not once per render.
  const [logLines, setLogLines] = useState<(LogLine & { ts: string })[]>([]);
  const pushLog = useCallback((line: LogLine) => {
    setLogLines((prev) => [...prev, { ...line, ts: new Date().toLocaleTimeString([], { hour12: false }) }]);
  }, []);

  const loadedIncidentId = data?.incident?.id;
  useEffect(() => {
    if (!loadedIncidentId) return;
    pushLog(triggerLogLine(incident));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedIncidentId]);

  useEffect(() => {
    if (!loadedIncidentId || stepIndex === 0) return;
    const line = stepLogLines[stepIndex - 1];
    if (line) pushLog(line);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedIncidentId, stepIndex]);

  useEffect(() => {
    if (!loadedIncidentId || !done) return;
    pushLog(resolutionLogLineForIncident(incident));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedIncidentId, done]);

  const logBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = logBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logLines.length]);

  const activeMockup = isMockupKey(channel) ? channel : null;

  const closeMockup = useCallback(() => navigate(`/outage-pipeline/${incidentId}`), [navigate, incidentId]);

  useEffect(() => {
    // Escape closes the topmost layer only — with a mockup open, that's the
    // mockup (back to the pipeline), matching the same fix in
    // OutageFanoutOverlay.tsx. Here "closing" means popping the URL rather
    // than clearing local state.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (activeMockup) closeMockup();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeMockup, closeMockup]);

  if (isLoading) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading incident…
      </div>
    );
  }

  if (error || !data?.incident) {
    return (
      <div className="state state--risk">
        <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h3 className="state__title">Incident not found</h3>
        <p className="state__body">Could not load incident {incidentId}.</p>
      </div>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">{isFtb ? 'FTB Automation' : 'Incident Response'}</p>
            <h1 className="pagehead__title">{incident.impact_statement || incident.service_name || 'Major Incident'}</h1>
            <div className="pagehead__facts">
              <span className="fact" aria-live="polite">
                {done
                  ? (isFtb ? 'FTB notice ready.' : 'All channels notified.')
                  : (isFtb ? 'Running FTB automation…' : 'Notifying incident response channels…')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {activeMockup && (
          <button type="button" className="pipeline-back" onClick={closeMockup}>
            ← Back to pipeline
          </button>
        )}

        <div className="pipeline-hero">
          <div className="pipeline-track" role="list" aria-label="Incident response pipeline">
            {steps.map((s, i) => {
              const state = i < stepIndex ? 'done' : i === stepIndex ? 'run' : 'todo';
              const clickable = state === 'done' && !!s.mockupKey;
              const color = STEP_COLORS[s.colorKey];

              const stageBody = (
                <>
                  <span
                    className="pipeline-stage-icon"
                    aria-hidden="true"
                    style={
                      state === 'todo'
                        ? undefined
                        : ({ color, '--stage-glow': `${color}66` } as CSSProperties)
                    }
                  >
                    <StepIcon colorKey={s.colorKey} />
                    {state === 'done' ? <DoneBadge /> : null}
                    {state === 'run' ? <span className="pipeline-stage-spinner" style={{ borderTopColor: color }} /> : null}
                  </span>
                  <span className="pipeline-stage-label">{s.label}</span>
                </>
              );

              return (
                <Fragment key={s.label}>
                  {i > 0 ? (
                    <div
                      className={`pipeline-connector${i <= stepIndex ? ' done' : ''}`}
                      style={{ '--connector-fill': color } as CSSProperties}
                    />
                  ) : null}
                  {clickable ? (
                    <Link
                      to={`/outage-pipeline/${incidentId}/${s.mockupKey}`}
                      className={`pipeline-stage ${state} clickable`}
                      role="listitem"
                    >
                      {stageBody}
                    </Link>
                  ) : (
                    <div className={`pipeline-stage ${state}`} role="listitem">
                      {stageBody}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="pipeline-foot">
          {done ? (
            <div className="pipeline-complete-banner">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <circle cx="8" cy="8" r="7" />
                <path d="M4.8 8.3 6.9 10.4 11.4 5.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {steps.length} / {steps.length} {isFtb ? 'automation steps' : 'response actions'} complete
            </div>
          ) : (
            <div className="pipeline-progress-banner">
              <span className="pipeline-progress-dot" aria-hidden="true" />
              {stepIndex} / {steps.length} {isFtb ? 'automation steps' : 'response actions'} complete&hellip; running
            </div>
          )}
        </div>

        <div className="pipeline-log" aria-label="Incident event log">
          <div className="pipeline-log-head">Event log</div>
          <div className="pipeline-log-body" ref={logBodyRef} aria-live="polite">
            {logLines.map((l, i) => (
              <div className="pipeline-log-line" key={i}>
                <span className="pipeline-log-ts">{l.ts}</span>
                <span className="pipeline-log-text" style={{ color: l.color }}>{l.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {activeMockup ? renderMockup(activeMockup, incident, closeMockup) : null}
    </>
  );
}
